import { useState } from "react";
import Avatar from "@amzn/awsui-chat-components-console/avatar";
import ChatBubble from "@amzn/awsui-chat-components-console/chat-bubble";
import Box from "@amzn/awsui-components-console/box";
import ButtonGroup from "@amzn/awsui-components-console/button-group";
import StatusIndicator from "@amzn/awsui-components-console/status-indicator";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Markdown from "markdown-to-jsx";

import { getString } from '../../../common/ui-string-labels/ui-strings-utils';
import { Message } from "../../../context/chatbot/ChatbotContext";
import chatbotConfig from "../chatbot-config.json";
import { MarkdownLink } from "./MarkdownLink";

import './ChatbotMessages.scss';

type CopyStatus = "idle" | "success" | "error";

const COPY_ACTION = "copy" as const;
const COPY_IDLE: CopyStatus = "idle";
const COPY_SUCCESS: CopyStatus = "success";
const COPY_ERROR: CopyStatus = "error";
const BOT_AVATAR_STYLE = "gen-ai" as const;

interface BotMessageProps {
    message: Message;
}

const BotMessage = ({ message }: BotMessageProps) => {
    const [copyStatus, setCopyStatus] = useState<CopyStatus>(COPY_IDLE);

    const handleCopy = () => {
        navigator.clipboard.writeText(message.text)
            .then(() => setCopyStatus(COPY_SUCCESS))
            .catch(() => setCopyStatus(COPY_ERROR));
    };

    return (
    <div data-testid="chatbot-bot-message">
    <ChatBubble
        key={message.id}
        type="incoming"
        ariaLabel={getString('chatbot.ariaLabels.assistantMessage')}
        avatar={
            <Avatar
                color={BOT_AVATAR_STYLE}
                iconName={BOT_AVATAR_STYLE}
                ariaLabel={getString('chatbot.ariaLabels.assistantAvatar')}
            />
        }
        actions={
            message.id !== chatbotConfig.initialMessage.id ? (
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <ButtonGroup
                            ariaLabel={getString('chatbot.buttonGroup.copy')}
                            variant="icon"
                            onItemClick={({ detail }) => {
                                if (detail.id === COPY_ACTION) {
                                    handleCopy();
                                }
                            }}
                            items={[
                                {
                                    type: "icon-button",
                                    id: COPY_ACTION,
                                    iconName: COPY_ACTION,
                                    text: getString('chatbot.buttonGroup.copy'),
                                    popoverFeedback: copyStatus === COPY_ERROR
                                        ? <StatusIndicator type={COPY_ERROR}>{getString('chatbot.buttonGroup.copyFailed')}</StatusIndicator>
                                        : <StatusIndicator type={COPY_SUCCESS}>{getString('chatbot.buttonGroup.copySuccess')}</StatusIndicator>,
                                },
                            ]}
                        />
                    </SpaceBetween>
                </Box>
            ) : undefined
        }
    >
        <Markdown className="chatbot-message-content" options={{ disableParsingRawHTML: true, wrapper: "div", overrides: { a: { component: MarkdownLink } } }}>
            {message.text}
        </Markdown>
    </ChatBubble>
    </div>
    );
};

export default BotMessage;
